export default class Contact {
  constructor() {
    // Handle ALL contact forms on the page
    const forms = document.querySelectorAll(".js-contact-form");
    if (!forms.length) return;

    // Initialize each form separately
    forms.forEach(form => this.initForm(form));
  }

  initForm(form) {
    const elements = {
      name: form.querySelector(".js-contact-name"),
      email: form.querySelector(".js-contact-email"),
      message: form.querySelector(".js-contact-message"),
      phone: form.querySelector(".js-contact-phone"),
      subject: form.querySelector(".js-contact-subject"),
      kvkk: form.querySelector(".js-contact-kvkk"),
      website: form.querySelector(".js-contact-website"),
      submit: form.querySelector(".js-contact-submit")
    };

    let isSubmitting = false;

    // Phone masking
    this.initPhoneMask(elements.phone);

    // Character counter
    this.initCharCounter(elements.message);

    // Real-time validation
    this.initRealTimeValidation(elements);

    // Privacy modal
    this.initPrivacyModal(elements.kvkk);

    // Form submission
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isSubmitting) return;

      const name = elements.name.value.trim();
      const email = elements.email.value.trim();
      const message = elements.message.value.trim();
      const phone = elements.phone.value.replace(/\D/g, "");
      const subject = elements.subject ? elements.subject.value.trim() : "";
      const kvkkConsent = elements.kvkk ? elements.kvkk.checked : false;
      const website = elements.website ? elements.website.value : "";

      // Validation
      let hasError = false;

      if (!name || name.length < 3) {
        this.showFieldError(elements.name, "Ad Soyad en az 3 karakter olmalıdır");
        hasError = true;
      }

      if (!email || !this.isValidEmail(email)) {
        this.showFieldError(elements.email, "Geçerli bir e-posta adresi girin");
        hasError = true;
      }

      if (!phone || phone.length !== 10) {
        this.showFieldError(elements.phone, "Geçerli bir telefon numarası girin");
        hasError = true;
      }

      if (!message || message.length < 10) {
        this.showFieldError(elements.message, "Mesaj en az 10 karakter olmalıdır");
        hasError = true;
      }

      if (!kvkkConsent) {
        this.showToast("KVKK Aydınlatma Metnini kabul etmelisiniz", "error");
        return;
      }

      if (hasError) {
        this.showToast("Lütfen tüm alanları doğru şekilde doldurun", "error");
        return;
      }

      // Show loading state
      this.setLoadingState(elements.submit, true);
      isSubmitting = true;

      try {
        const res = await fetch("/api/contact-requests/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            full_name: name,
            email: email,
            phone: "+90" + phone,
            subject: subject,
            message: message,
            kvkk_consent: kvkkConsent,
            website: website,
          }),
        });

        if (res.ok) {
          this.showToast("Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.", "success");
          form.reset();

          // Reset char counter
          const charCount = form.querySelector(".js-char-count");
          if (charCount) charCount.textContent = "0";
        } else {
          const errData = await res.json().catch(() => ({}));
          this.showToast(
            errData.detail || "Bir hata oluştu. Lütfen tekrar deneyin.",
            "error"
          );
        }
      } catch (err) {
        this.showToast(
          "Sunucuya bağlanılamadı. Lütfen daha sonra deneyin.",
          "error"
        );
      } finally {
        this.setLoadingState(elements.submit, false);
        isSubmitting = false;
      }
    });
  }

  initPhoneMask(phoneEl) {
    if (!phoneEl) return;

    phoneEl.addEventListener("input", (e) => {
      let value = e.target.value.replace(/\D/g, "");

      // Auto-add +90 if starts with 5
      if (value.startsWith("5")) {
        value = "90" + value;
      }

      // Remove +90 prefix for formatting
      if (value.startsWith("90")) {
        value = value.substring(2);
      }

      // Format: (5XX) XXX XX XX
      let formatted = "";
      if (value.length > 0) {
        formatted = "(" + value.substring(0, 3);
      }
      if (value.length >= 4) {
        formatted += ") " + value.substring(3, 6);
      }
      if (value.length >= 7) {
        formatted += " " + value.substring(6, 8);
      }
      if (value.length >= 9) {
        formatted += " " + value.substring(8, 10);
      }

      e.target.value = formatted;
    });
  }

  initCharCounter(msgEl) {
    if (!msgEl) return;

    const maxChars = 1000;
    const wrapper = msgEl.closest(".contact-form__field, .contact__field");
    if (!wrapper) return;

    // Create counter element
    const counter = document.createElement("div");
    counter.className = "contact-form__char-counter contact__char-counter";
    counter.innerHTML = `<span class="js-char-count">0</span>/${maxChars}`;
    wrapper.appendChild(counter);

    const countEl = counter.querySelector(".js-char-count");

    msgEl.addEventListener("input", (e) => {
      const length = e.target.value.length;
      countEl.textContent = length;

      if (length > maxChars) {
        e.target.value = e.target.value.substring(0, maxChars);
        countEl.textContent = maxChars;
      }

      // Color change based on length
      if (length > maxChars * 0.9) {
        counter.classList.add("--warning");
      } else {
        counter.classList.remove("--warning");
      }
    });
  }

  initRealTimeValidation(elements) {
    // Name validation
    if (elements.name) {
      elements.name.addEventListener("blur", () => {
        const value = elements.name.value.trim();
        if (value && value.length < 3) {
          this.showFieldError(elements.name, "Ad Soyad en az 3 karakter olmalıdır");
        } else if (value) {
          this.clearFieldError(elements.name);
        }
      });
    }

    // Email validation
    if (elements.email) {
      elements.email.addEventListener("blur", () => {
        const value = elements.email.value.trim();
        if (value && !this.isValidEmail(value)) {
          this.showFieldError(elements.email, "Geçerli bir e-posta adresi girin");
        } else if (value) {
          this.clearFieldError(elements.email);
        }
      });
    }

    // Phone validation
    if (elements.phone) {
      elements.phone.addEventListener("blur", () => {
        const value = elements.phone.value.trim();
        const digitsOnly = value.replace(/\D/g, "");
        if (value && digitsOnly.length !== 10) {
          this.showFieldError(elements.phone, "Telefon numarası 10 haneli olmalıdır");
        } else if (value) {
          this.clearFieldError(elements.phone);
        }
      });
    }

    // Message validation
    if (elements.message) {
      elements.message.addEventListener("blur", () => {
        const value = elements.message.value.trim();
        if (value && value.length < 10) {
          this.showFieldError(elements.message, "Mesaj en az 10 karakter olmalıdır");
        } else if (value) {
          this.clearFieldError(elements.message);
        }
      });
    }
  }

  showFieldError(field, message) {
    const wrapper = field.closest(".contact-form__field, .contact__field");
    if (!wrapper) return;

    // Remove existing error
    this.clearFieldError(field);

    // Add error class
    wrapper.classList.add("--error");
    field.classList.add("--error");

    // Create error message
    const errorEl = document.createElement("div");
    errorEl.className = "contact-form__error contact__error";
    errorEl.textContent = message;
    wrapper.appendChild(errorEl);
  }

  clearFieldError(field) {
    const wrapper = field.closest(".contact-form__field, .contact__field");
    if (!wrapper) return;

    wrapper.classList.remove("--error");
    field.classList.remove("--error");

    const errorEl = wrapper.querySelector(".contact-form__error, .contact__error");
    if (errorEl) {
      errorEl.remove();
    }
  }

  initPrivacyModal(kvkkEl) {
    import('./modal.js').then(({ default: Modal }) => {
      const privacyModal = new Modal(".js-privacy-modal");

      const privacyLinks = document.querySelectorAll(".js-privacy-link");
      privacyLinks.forEach(link => {
        link.addEventListener("click", (e) => {
          e.preventDefault();
          privacyModal.open();
        });
      });

      const originalClose = privacyModal.close.bind(privacyModal);
      privacyModal.close = () => {
        originalClose();
        if (kvkkEl && !kvkkEl.checked) {
          kvkkEl.checked = true;
        }
      };
    });
  }

  setLoadingState(submitBtn, loading) {
    if (!submitBtn) return;

    if (loading) {
      submitBtn.classList.add("--loading");
      submitBtn.disabled = true;

      const btnText = submitBtn.querySelector("span");
      if (btnText) {
        btnText.dataset.originalText = btnText.textContent;
        btnText.textContent = "Gönderiliyor...";
      }
    } else {
      submitBtn.classList.remove("--loading");
      submitBtn.disabled = false;

      const btnText = submitBtn.querySelector("span");
      if (btnText && btnText.dataset.originalText) {
        btnText.textContent = btnText.dataset.originalText;
      }
    }
  }

  showToast(message, type = "success") {
    // Remove existing toasts
    const existingToast = document.querySelector(".contact-toast");
    if (existingToast) {
      existingToast.remove();
    }

    // Create toast
    const toast = document.createElement("div");
    toast.className = `contact-toast contact-toast--${type}`;

    const icon = type === "success"
      ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>'
      : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>';

    toast.innerHTML = `
      <div class="contact-toast__icon">${icon}</div>
      <div class="contact-toast__message">${message}</div>
      <button class="contact-toast__close" aria-label="Kapat">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => toast.classList.add("--show"), 10);

    // Close button
    const closeBtn = toast.querySelector(".contact-toast__close");
    closeBtn.addEventListener("click", () => this.hideToast(toast));

    // Auto hide after 5 seconds
    setTimeout(() => this.hideToast(toast), 5000);
  }

  hideToast(toast) {
    toast.classList.remove("--show");
    setTimeout(() => toast.remove(), 300);
  }

  isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  }
}
